import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, FieldSelect, FieldText, HelperField, ModalBody, ModalFoot, ModalHead, Text, } from '@dixa/lib-shared-habla';
import omit from 'lodash/omit';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { useIsChanged } from 'components/powerFlowShared/hooks/useIsChanged';
import { AdapterTypes } from 'components/shared/announcementPicker/adapters';
import { AnnouncementPicker } from 'components/shared/announcementPicker/announcementPicker';
import styles from './ivr.module.scss';
import { ButtonOption } from './ivr/buttonOption';
const BUTTONS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];
const ANNOUNCEMENT_FIELD = 'announcement';
const OPTIONS_FIELD = 'options';
export function IVR({ node, replaceNode, triggerClose }) {
    var _a, _b, _c;
    const { announcement: { file } = { file: null }, buttons, default_node_id = '', repeat: payloadRepeat, } = Object.assign({}, node.payload);
    const initAnnouncement = {
        label: (_a = file === null || file === void 0 ? void 0 : file.pretty_name) !== null && _a !== void 0 ? _a : '',
        url: (_b = file === null || file === void 0 ? void 0 : file.url) !== null && _b !== void 0 ? _b : '',
        author: (_c = file === null || file === void 0 ? void 0 : file.author) !== null && _c !== void 0 ? _c : '',
    };
    const initButtonOptions = BUTTONS.map((button) => {
        const { nodeId } = (buttons === null || buttons === void 0 ? void 0 : buttons[button]) || {};
        return {
            button,
            active: !!nodeId,
            nodeId: nodeId || uuid(),
        };
    });
    const initDefaultNodeId = default_node_id || '';
    const initRepeat = payloadRepeat || 3;
    const [announcement, setAnnouncement] = useState(initAnnouncement);
    const [buttonOptions, setButtonOptions] = useState(initButtonOptions);
    const [defaultNodeId, setDefaultNodeId] = useState(initDefaultNodeId);
    const [repeat, setRepeat] = useState(initRepeat);
    const [errors, setErrors] = useState({});
    const isChanged = useIsChanged([initAnnouncement, announcement], [initButtonOptions, buttonOptions], [initDefaultNodeId, defaultNodeId], [initRepeat, repeat]);
    const hasActiveButton = useMemo(() => buttonOptions.some((option) => option.active), [buttonOptions]);
    const buttonItems = useMemo(() => [
        { id: '', name: 'Hang up' },
        ...buttonOptions.reduce((buttons, { button, nodeId, active }) => {
            if (!active)
                return buttons;
            return [...buttons, { id: nodeId, name: `Press ${button}` }];
        }, []),
    ], [buttonOptions]);
    useEffect(() => {
        if (announcement.url && errors[ANNOUNCEMENT_FIELD]) {
            setErrors((errors) => omit(errors, ANNOUNCEMENT_FIELD));
        }
        if (hasActiveButton && errors[OPTIONS_FIELD]) {
            setErrors((errors) => omit(errors, OPTIONS_FIELD));
        }
    }, [announcement, errors, hasActiveButton, setErrors]);
    const handleButtonChange = useCallback((option) => {
        setButtonOptions((bOptions) => {
            const newButtonOptions = [...bOptions];
            const index = newButtonOptions.findIndex(({ button }) => button === option.button);
            newButtonOptions[index] = option;
            return newButtonOptions;
        });
    }, [setButtonOptions]);
    const handleDefaultOptionChange = useCallback((value) => setDefaultNodeId(value), [setDefaultNodeId]);
    const handleRepeatChange = useCallback((event) => {
        if (!event) {
            return;
        }
        const { target: { value }, } = event;
        setRepeat(parseInt(value));
    }, [setRepeat]);
    const validate = useCallback(() => {
        const fieldErrors = {};
        if (!announcement.url) {
            fieldErrors[ANNOUNCEMENT_FIELD] = 'Please select an announcement file';
        }
        if (!hasActiveButton) {
            fieldErrors[OPTIONS_FIELD] = 'Please select at least one menu option';
        }
        setErrors(fieldErrors);
        return !Object.keys(fieldErrors).length;
    }, [announcement, hasActiveButton]);
    const handleSubmit = useCallback(() => {
        if (!validate()) {
            return;
        }
        const payload = {
            announcement: {
                file: announcement,
                tts: null,
            },
            buttons: buttonOptions.reduce((buttons, { active, button, nodeId }) => {
                if (active) {
                    buttons[button] = {
                        button,
                        nodeId: nodeId !== null && nodeId !== void 0 ? nodeId : uuid(),
                    };
                }
                return buttons;
            }, {}),
            repeat,
            default_node_id: defaultNodeId,
        };
        replaceNode(Object.assign(Object.assign({}, node), { icon: 'i-channels', isAction: false, isEditable: true, isRemovable: true, nodeType: FlowNodeTypes.IVR_MENU, overlay: [], payload, text: 'IVR' }));
    }, [
        announcement,
        buttonOptions,
        defaultNodeId,
        node,
        repeat,
        replaceNode,
        validate,
    ]);
    return (_jsxs(Box, Object.assign({ width: "600px" }, { children: [_jsx(ModalHead, Object.assign({ title: "Interactive Voice Reponse (IVR)", onClose: triggerClose }, { children: _jsx(Text.Paragraph, Object.assign({ className: styles.description }, { children: "Give incoming callers options to choose from using their dial pad before talking to an agent." })) })), _jsx(ModalBody, { children: _jsxs("form", { children: [_jsx(AnnouncementPicker, { adapter: AdapterTypes.Announcement, className: styles.announcementPicker, onSelect: setAnnouncement, selectedAudio: announcement, fieldProps: {
                                meta: { error: errors[ANNOUNCEMENT_FIELD] },
                            } }), _jsxs("div", Object.assign({ className: styles.options }, { children: [_jsx("h5", Object.assign({ className: styles.optionsTitle }, { children: "Menu options" })), _jsx("div", Object.assign({ className: styles.optionsWrapper }, { children: buttonOptions.map((buttonOption) => (_jsx(ButtonOption, { onChange: handleButtonChange, option: buttonOption }, buttonOption.button))) }))] })), _jsx(HelperField, Object.assign({ className: styles.optionsError, errorMessage: errors[OPTIONS_FIELD], fieldId: "options" }, { children: _jsx(_Fragment, {}) })), _jsx(FieldSelect, { className: styles.defaultOption, input: {
                                name: 'default option',
                                onChange: handleDefaultOptionChange,
                                value: defaultNodeId,
                            }, items: buttonItems, label: "Default option", noAlert: true }), _jsx(FieldText, { input: {
                                name: 'repeat',
                                onChange: handleRepeatChange,
                                value: repeat.toString(),
                            }, label: "Repeat", max: 10, min: 1, noAlert: true, suffix: "Times", type: "number" })] }) }), _jsx(ModalFoot, { confirmOnClick: handleSubmit, confirmName: getConfirmButtonVerb(node), confirmProps: { disabled: !isChanged }, cancelOnClick: triggerClose })] })));
}
